import { useCallback, useEffect, useState } from 'react';
import { AccountPausedScreen } from '../auth/AccountPausedScreen';
import { getCurrentOrganization, TravelBallAdminError } from '../../services/travelBallAdmin';
import type { LegalAcceptanceRequired } from '../../types/legal';
import { LegalAcceptanceGate } from './LegalAcceptanceGate';

/** Shown when the API says the program is paused but sends no copy of its own. */
const PROGRAM_PAUSED_FALLBACK =
  'This travel ball program is paused. Please contact your program admin or RosterPilot.';

type PauseState =
  | { status: 'checking' }
  | { status: 'clear' }
  | { status: 'paused'; message: string | null; programName: string | null };

type PauseCheck = { paused: false } | { paused: true; message: string | null; programName: string | null };

/**
 * Reads the program's pause state from `GET /api/travel-ball/organizations/current`, which the API
 * leaves open while a program is paused or acceptance is pending. Throws when the read failed for
 * any reason other than the pause itself.
 */
const readProgramPause = async (): Promise<PauseCheck> => {
  try {
    const organization = await getCurrentOrganization();
    // The read carries no pause copy, so the screen shows the fallback; only a refused write
    // (below) carries the admin's own message.
    return organization.isPaused
      ? { paused: true, message: null, programName: organization.name }
      : { paused: false };
  } catch (error) {
    if (error instanceof TravelBallAdminError && error.code === 'travel_ball_organization_paused') {
      return { paused: true, message: error.pauseMessage, programName: null };
    }
    throw error;
  }
};

/** On arrival a failed read falls through to the dialog, never to a lockout on a guess. */
const checkProgramPause = (): Promise<PauseCheck> =>
  readProgramPause().catch((): PauseCheck => ({ paused: false }));

/**
 * The legal acceptance dialog for a travel ball view: accepts for the travel ball identity at the
 * travel ball endpoint.
 *
 * In a program view (`checkProgramPause`) a paused program wins, as it does on the API (the pause
 * gate runs first and refuses the accept call too): the program's pause state is read before the
 * dialog shows, and a pause that lands while it is open swaps in the paused screen. A player or
 * parent/guardian view has no program to pause, so it goes straight to the dialog.
 */
export const TravelBallLegalGate = ({
  required,
  checkProgramPause: shouldCheckPause,
}: {
  required: LegalAcceptanceRequired;
  checkProgramPause: boolean;
}) => {
  const [pause, setPause] = useState<PauseState>(() =>
    shouldCheckPause ? { status: 'checking' } : { status: 'clear' }
  );

  useEffect(() => {
    if (!shouldCheckPause) return;
    let cancelled = false;
    void checkProgramPause().then((result) => {
      if (cancelled) return;
      setPause(
        result.paused
          ? { status: 'paused', message: result.message, programName: result.programName }
          : { status: 'clear' }
      );
    });
    return () => {
      cancelled = true;
    };
  }, [shouldCheckPause]);

  const handleProgramPaused = useCallback((message: string | null) => {
    setPause({ status: 'paused', message, programName: null });
  }, []);

  // "Check again" on the paused screen: resolves while still paused, so the screen says so, and
  // throws when the check itself failed, so the screen says that instead of guessing.
  const handleCheckAgain = useCallback(async () => {
    const result = await readProgramPause();
    if (!result.paused) setPause({ status: 'clear' });
  }, []);

  if (pause.status === 'checking') {
    return (
      <div className="legal-gate" role="status" data-testid="tb-legal-gate-checking">
        Loading...
      </div>
    );
  }

  if (pause.status === 'paused') {
    return (
      <AccountPausedScreen
        message={pause.message ?? PROGRAM_PAUSED_FALLBACK}
        organizationName={pause.programName}
        onCheckAgain={handleCheckAgain}
      />
    );
  }

  return (
    <LegalAcceptanceGate
      required={required}
      audience="travelball"
      onProgramPaused={shouldCheckPause ? handleProgramPaused : undefined}
    />
  );
};
